import { IApiClientOptions } from 'core';
import { IGetCommandQuery } from 'dto/remote-commands';
import { IDeviceInfoProps, ISoftwareUpdateJobSummary } from 'store/devices-store';
import {
    DeviceTypeDto,
    ICommissioningUploadRequest,
    ICryptoReportResult,
    IDataTransferResult,
    IDecommissionDevicesOptions,
    IDecommissionDevicesResponse,
    IDeviceAttributesResult,
    IDeviceCommissioningResponse,
    IDevicePackageResponse,
    IDeviceTimeOptions,
    IExecutionJobSummaryResponse,
    IGetDeviceLogsOptions,
    IHistoryTimeRange,
    ILogOptions,
    IProcessHistory,
    IResetDeviceResult,
    IScrFileOptions,
    IScrInfoResult,
    ISearchDeviceInfo,
    ISearchDeviceOptions,
    IShellCommandExecuteParams,
    IShellCommandExecutionResponse,
    ITimeReportRequestParams,
    IUpdateDeviceAttributeResponse,
} from '../dto/device-dto';
import { AssureBaseApiClient, HTTP_METHOD } from './assure-base-api-client';
import { transform } from 'dto/transformer';
import { EffectiveDeploymentDto, InstalledComponentDto } from 'dto/raptor-device.dto';
import { ContainerDto } from 'dto/container.dto';

export interface IUpdateDeviceAttribute {
    deviceName: string;
    attributeName: string;
    attributeValue: string;
}

export class DevicesApiClient extends AssureBaseApiClient {
    constructor(options: IApiClientOptions) {
        super('devices', options);
    }

    public async getDevicesByName(accountId: number, deviceName: string): Promise<IDeviceInfoProps> {
        return this.send<IDeviceInfoProps>(HTTP_METHOD.GET, this.getRequestOptionsWithAccountId(accountId, deviceName));
    }

    public async searchDevices(accountId: number, params?: ISearchDeviceOptions): Promise<ISearchDeviceInfo[]> {
        const result = await this.send<{ devices: ISearchDeviceInfo[] }>(
            HTTP_METHOD.GET,
            this.getRequestOptionsWithAccountId(accountId, '', params),
        );
        return result.devices;
    }

    public async getProvisionedDevices(accountId: number, params?: ISearchDeviceOptions): Promise<ISearchDeviceInfo[]> {
        const result = await this.send<{ devices: ISearchDeviceInfo[] }>(
            HTTP_METHOD.GET,
            this.getRequestOptionsWithAccountId(accountId, 'provisioned', params),
        );
        return result.devices;
    }

    public async getDerivationIds(accountId: number, accountUuid: string): Promise<{ derivationIds: string[] }> {
        return this.send<{ derivationIds: string[] }>(
            HTTP_METHOD.GET,
            this.getRequestOptionsWithAccountId(accountId, `${accountUuid}/derivation-ids`),
        );
    }

    public async getCommissionedDevices(
        accountId: number,
        params?: ISearchDeviceOptions,
    ): Promise<ISearchDeviceInfo[]> {
        const result = await this.send<{ devices: ISearchDeviceInfo[] }>(
            HTTP_METHOD.GET,
            this.getRequestOptionsWithAccountId(accountId, 'commissioned', params),
        );
        return result.devices;
    }

    public getDevicesTimeReport(accountId: number, params?: ITimeReportRequestParams): Promise<IDeviceTimeOptions> {
        return this.send<IDeviceTimeOptions>(
            HTTP_METHOD.GET,
            this.getRequestOptionsWithAccountId(accountId, 'commissioned/time-report', params),
        );
    }

    public getCommissionedDevicesHistory(accountId: number, params?: IHistoryTimeRange): Promise<{ [accountUuid: string]: number }> {
        return this.send<{ [accountUuid: string]: number }>(
            HTTP_METHOD.GET,
            this.getRequestOptionsWithAccountId(accountId, 'commissioned/history', params),
        );
    }

    public async getCurrentPackageListing(accountId: number, deviceName: string): Promise<IDevicePackageResponse> {
        return this.send<IDevicePackageResponse>(
            HTTP_METHOD.GET,
            this.getRequestOptionsWithAccountId(accountId, `${deviceName}/packages`),
        );
    }

    public async updateAttribute(
        accountId: number,
        options: IUpdateDeviceAttribute,
    ): Promise<IUpdateDeviceAttributeResponse> {
        const { deviceName, ...rest } = options;
        return this.send<IUpdateDeviceAttributeResponse>(
            HTTP_METHOD.POST,
            this.getRequestOptionsWithAccountId(accountId, `${deviceName}/update-attribute`, rest),
        );
    }

    public async getDeviceJobs(
        accountId: number,
        deviceName: string,
        query?: IGetCommandQuery,
    ): Promise<IExecutionJobSummaryResponse> {
        const result = await this.send<IExecutionJobSummaryResponse>(
            HTTP_METHOD.GET,
            this.getRequestOptionsWithAccountId(accountId, `${deviceName}/jobs`, this._convertToValidQuery(query)),
        );
        return result;
    }

    public async getDeviceSoftwareUpdates(accountId: number, deviceName: string, dateFrom?: string, dateTo?: string): Promise<ISoftwareUpdateJobSummary[]> {
        const result = await this.send<{ history: ISoftwareUpdateJobSummary[] }>(
            HTTP_METHOD.GET,
            this.getRequestOptionsWithAccountId(accountId, `${deviceName}/software-updates`, dateFrom && dateTo ? {
                dateFrom: dateFrom,
                dateTo: dateTo,
            } : {}),
        );
        return result.history;
    }

    public async getDeviceProcesses(accountId: number, deviceName: string): Promise<IProcessHistory[]> {
        const result = await this.send<{ processes: IProcessHistory[] }>(
            HTTP_METHOD.GET,
            this.getRequestOptionsWithAccountId(accountId, `${deviceName}/processes/history`),
        );
        return result.processes;
    }

    public async getCurrentDeviceServices(accountId: number, deviceName: string): Promise<string[]> {
        const result = await this.send<{ services: { service_list: string[] } }>(
            HTTP_METHOD.GET,
            this.getRequestOptionsWithAccountId(accountId, `${deviceName}/services`),
        );
        return result.services?.service_list || [];
    }

    public async getCurrentDeviceScrFiles(accountId: number, deviceName: string): Promise<IScrFileOptions[]> {
        const result = await this.send<{ scrFiles: IScrFileOptions[] }>(
            HTTP_METHOD.GET,
            this.getRequestOptionsWithAccountId(accountId, `${deviceName}/scr-files`),
        );
        return result.scrFiles;
    }

    public async getTransferredFiles(accountId: number, deviceName: string): Promise<{ transferredFiles: unknown[] }> {
        return this.send<{ transferredFiles: unknown[] }>(
            HTTP_METHOD.GET,
            this.getRequestOptionsWithAccountId(accountId, `${deviceName}/transferred-files`),
        );
    }

    public async getContainers(accountId: number, deviceName: string): Promise<ContainerDto[]> {
        const response = await this.send<{ containers: unknown[] }>(
            HTTP_METHOD.GET,
            this.getRequestOptionsWithAccountId(accountId, `${deviceName}/containers`),
        );
        return transform(ContainerDto, response.containers);
    }

    public async getHistoricalOfFileTransferExecutionResult(accountId: number, deviceName: string, dateFrom?: string, dateTo?: string): Promise<{ executions: unknown[] }> {
        return this.send<{ executions: unknown[] }>(
            HTTP_METHOD.GET,
            this.getRequestOptionsWithAccountId(accountId, `${deviceName}/file-transfer-executions/history`, dateFrom && dateTo ? {
                dateFrom: dateFrom,
                dateTo: dateTo,
            } : {}),
        );
    }
    public async distributeGPGKeys(accountId: number, deviceName: string): Promise<string> {
        const result = await this.send<{ excutionId: string }>(
            HTTP_METHOD.POST,
            this.getRequestOptionsWithAccountId(accountId, `${deviceName}/gpg-keys/distribute`),
        );
        return result.excutionId;
    }

    public async distributeExternalRepoConfiguration(accountId: number, deviceName: string): Promise<{ message: string }> {
        return this.send<{ message: string }>(
            HTTP_METHOD.POST,
            this.getRequestOptionsWithAccountId(accountId, `${deviceName}/external-repo-config/distribute`),
        );
    }

    public async getDeviceLogs(accountId: number, deviceName: string, logTypeName: string, options?: IGetDeviceLogsOptions): Promise<ILogOptions[]> {
        const result = await this.send<{ logEntries: ILogOptions[] }>(
            HTTP_METHOD.GET,
            this.getRequestOptionsWithAccountId(accountId, `${deviceName}/logs/${logTypeName}`, options),
        );
        return result.logEntries;
    }

    public async getTaipanBatteryInfo(accountId: number, deviceName: string): Promise<any> {
        return this.send<any>(
            HTTP_METHOD.GET,
            this.getRequestOptionsWithAccountId(accountId, `${deviceName}/taipan-battery-info`),
        );
    }

    public async getDeviceConnectivity(accountId: number, deviceName: string): Promise<any> {
        return this.send<any>(
            HTTP_METHOD.GET,
            this.getRequestOptionsWithAccountId(accountId, `${deviceName}/connectivity`),
        );
    }

    public async uploadCommissioningDevices(accountId: number, params: ICommissioningUploadRequest): Promise<any> {
        const result = await this.send<{ devices: any }>(
            HTTP_METHOD.POST,
            this.getRequestOptionsWithAccountId(accountId, 'upload-commissioning', params),
        );
        return result.devices;
    }

    public async commissionDevices(
        accountId: number,
        clientAccountId: number,
        deviceGroupName: string,
        welcomePackId: number,
        identifiers: string[],
    ): Promise<IDeviceCommissioningResponse[]> {
        const response = await this.send<{
            result: IDeviceCommissioningResponse[]
        }>(HTTP_METHOD.POST, this.getRequestOptionsWithAccountId(accountId, 'commissioning', {
            clientAccountId,
            deviceGroupName,
            welcomePackId,
            identifiers
        }));
        return response.result; // this auth always returns status=sucess
    }

    public dataTransfer(
        accountId: number,
        deviceName: string,
        deviceGroup: string,
        filePath: string,
        permissions: string,
        files: File[],
    ): Promise<IDataTransferResult> {
        return this.send<IDataTransferResult>(
            HTTP_METHOD.POST,
            this.getRequestOptionsWithAccountId(accountId, 'data-transfer', {
                deviceName,
                deviceGroup,
                filePath,
                permissions,
                files,
            }),
        );
    }

    public async shellCommandExecution(
        accountId: number,
        params: IShellCommandExecuteParams,
    ): Promise<IShellCommandExecutionResponse[]> {
        const result = await this.send<{ shellCommandOutput: IShellCommandExecutionResponse[] }>(
            HTTP_METHOD.POST,
            this.getRequestOptionsWithAccountId(accountId, 'shell-command', params),
        );
        return result.shellCommandOutput;
    }

    public async getDeviceCryptoReport(accountId: number, deviceName: string): Promise<ICryptoReportResult> {
        const result = await this.send<{ cryptoReport: ICryptoReportResult }>(
            HTTP_METHOD.GET,
            this.getRequestOptionsWithAccountId(accountId, `${deviceName}/crypto-report`),
        );
        return result.cryptoReport;
    }

    public decommissionDevices(
        accountId: number,
        params: IDecommissionDevicesOptions,
    ): Promise<IDecommissionDevicesResponse> {
        return this.send<IDecommissionDevicesResponse>(
            HTTP_METHOD.POST,
            this.getRequestOptionsWithAccountId(accountId, 'decommission', params),
        );
    }

    public resetDevice(accountId: number, deviceName: string): Promise<IResetDeviceResult> {
        return this.send<IResetDeviceResult>(
            HTTP_METHOD.POST,
            this.getRequestOptionsWithAccountId(accountId, `${deviceName}/reset`),
        );
    }

    public async getCurrentDeviceScrInfo(accountId: number, deviceName: string): Promise<IScrInfoResult> {
        const result = await this.send<{ scrInfo: IScrInfoResult }>(
            HTTP_METHOD.GET,
            this.getRequestOptionsWithAccountId(accountId, `${deviceName}/scr-info`),
        );
        return result.scrInfo;
    }

    public async getDeviceAttributes(accountId: number, deviceName: string): Promise<IDeviceAttributesResult> {
        return this.send<IDeviceAttributesResult>(
            HTTP_METHOD.GET,
            this.getRequestOptionsWithAccountId(accountId, `${deviceName}/attributes`),
        );
    }

    public async getInstalledFirmwareVersions(accountId: number): Promise<string[]> {
        const result = await this.send<{ installedFirmwareVersions: string[]; }>(
            HTTP_METHOD.GET,
            this.getRequestOptionsWithAccountId(accountId, 'installed-firmwares'),
        );
        return result.installedFirmwareVersions;
    }

    public async getSupportedTypes(accountId: number, platform?: string): Promise<DeviceTypeDto[]> {
        const result = await this.send<{ supportedTypes: string[]; }>(
            HTTP_METHOD.GET,
            this.getRequestOptionsWithAccountId(accountId, 'supported-types', { platform }),
        );
        return transform(DeviceTypeDto, result.supportedTypes);
    }

    public async getInstalledComponents(accountId: number, deviceName: string): Promise<InstalledComponentDto[]> {
        const result = await this.send<{ installedComponents: InstalledComponentDto[] }>(
            HTTP_METHOD.GET,
            this.getRequestOptionsWithAccountId(accountId, `${deviceName}/installed-components`),
        );
        return result.installedComponents || [];
    }

    public async getEffectiveDeployments(accountId: number, deviceName: string): Promise<EffectiveDeploymentDto[]> {
        const result = await this.send<{ effectiveDeployments: EffectiveDeploymentDto[] }>(
            HTTP_METHOD.GET,
            this.getRequestOptionsWithAccountId(accountId, `${deviceName}/effective-deployments`),
        );
        return result.effectiveDeployments || [];
    }

    protected getRootPath(): string {
        return 'devices';
    }
}
